"use client";

import Meta from "@/components/Meta";
import { AppButton } from "@/components/button/AppButton";
import PhoneInput, { PhoneInputValue } from "@/components/form/PhoneInput";
import DataFetchingSpinner from "@/components/layouts/components/spinners/DataFetchingSpinner";
import useTranslation from "@/hooks/useTranslation";
import { getGroupLogo } from "@/lib/api/group";
import { useAxios } from "@/lib/http/axios-client";
import { useAuth } from "@/providers/auth.provider";
import { isEmpty } from "@/utils/null-safety.utils";
import { url } from "@/utils/url.helper";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Label, TextInput } from "flowbite-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react"; // Re-added hooks
import { MdOutlineEmail, MdSmartphone } from "react-icons/md";

type LoginMethod = "email" | "phone";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+41");
  const [countryISO2, setCountryISO2] = useState("CH");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");
  const [submitError, setSubmitError] = useState("");
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const searchParams = useSearchParams();
  const [groupId, setGroupId] = useState(searchParams.get("gid"));
  const [invitationCode, setInvitationCode] = useState(
    searchParams.get("invitation_code"),
  );
  const axiosClient = useAxios();
  const router = useRouter();
  const isInitialLoad = useRef(true);
  const [isInvitationRedirect, setIsInvitationRedirect] = useState(false);

  // Helper function to update URL with login method while preserving other query params
  const updateUrlWithLoginMethod = useCallback(
    (method: LoginMethod) => {
      const params: Record<string, string> = { by: method };

      // Preserve other query params except 'by'
      searchParams.forEach((value, key) => {
        if (key !== "by") {
          params[key] = value;
        }
      });

      return url("/login", params);
    },
    [searchParams],
  );

  // Save login method to localStorage
  const saveLoginMethodToStorage = useCallback((method: LoginMethod) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("preferredLoginMethod", method);
    }
  }, []);

  // Save country code to localStorage
  const saveCountryCodeToStorage = useCallback((countryCode: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("preferredCountryCode", countryCode);
    }
  }, []);
  const saveCountryISO2ToStorage = useCallback((countryISO2: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("preferredCountryISO2", countryISO2);
    }
  }, []);

  // Read login method and country code from URL parameter or localStorage
  useEffect(() => {
    // First check URL parameter
    const byParam = searchParams.get("by");

    if (byParam === "email" || byParam === "phone") {
      // Set method from URL param
      setLoginMethod(byParam);
      saveLoginMethodToStorage(byParam);
      setSubmitError("");
      setIsLoading(false); // No longer loading once we have the method
    } else if (isInitialLoad.current && typeof window !== "undefined") {
      // Only on initial load, check localStorage if URL param not present
      isInitialLoad.current = false;
      const savedMethod = localStorage.getItem(
        "preferredLoginMethod",
      ) as LoginMethod | null;

      if (savedMethod === "email" || savedMethod === "phone") {
        setLoginMethod(savedMethod);
        // Update URL to match saved preference
        router.replace(updateUrlWithLoginMethod(savedMethod));
        setIsLoading(false); // No longer loading once we have the method
      } else {
        // Default to email if nothing is saved
        setLoginMethod("email");
        router.replace(updateUrlWithLoginMethod("email"));
        setIsLoading(false); // No longer loading with default method
      }
    }

    // Load saved country code from localStorage on initial load
    if (typeof window !== "undefined") {
      const savedCountryCode = localStorage.getItem("preferredCountryCode");
      const savedCountryISO2 = localStorage.getItem("preferredCountryISO2");
      if (savedCountryCode && savedCountryISO2) {
        setCountryCode(savedCountryCode);
        setCountryISO2(savedCountryISO2);
      }
    }
  }, [
    searchParams,
    router,
    saveLoginMethodToStorage,
    updateUrlWithLoginMethod,
    setSubmitError,
    saveCountryCodeToStorage,
  ]);

  const handleOnPhoneInputChange = (value: PhoneInputValue) => {
    setPhoneNumber(value.phoneValue);
    setCountryCode(value.countryCodeValue);
    setCountryISO2(value.countryCodeISO2Value);
    // Save country code & ISO2 to localStorage when it changes
    if (value.countryCodeValue) {
      saveCountryCodeToStorage(value.countryCodeValue);
    }
    if (value.countryCodeISO2Value) {
      saveCountryISO2ToStorage(value.countryCodeISO2Value);
    }
  };

  const { mutate, isPending, isError, error, reset } = useMutation({
    mutationFn: (data: SendOtpRequest) =>
      axiosClient.post("/auth/send-otp", data),
    onSuccess: (data) => {
      // Standard OTP flow
      let queryParams: Record<string, string> = {};
      if (loginMethod === "email") {
        queryParams.email = email;
        queryParams.by = "email";
      } else {
        queryParams.phone = countryCode + phoneNumber;
        queryParams.by = "phone";
      }
      invitationCode && (queryParams.invitation_code = invitationCode);
      groupId && (queryParams.gid = groupId);

      // Build verify URL preserving other query params (except for any that we're explicitly setting)
      const existingParams = new URLSearchParams(searchParams.toString());
      existingParams.delete("email");
      existingParams.delete("phone");
      existingParams.delete("by");
      existingParams.delete("invitation_code");
      existingParams.delete("gid");

      const existingParamsString = existingParams.toString();
      router.push(
        url("/verify", queryParams) +
          (existingParamsString ? `&${existingParamsString}` : ""),
      );
    },
    onError: (error) => setSubmitError((error as any).response.data.message),
  });

  // Helper to switch login methods
  const switchLoginMethod = useCallback(
    (method: LoginMethod) => {
      setLoginMethod(method);
      saveLoginMethodToStorage(method);
      setSubmitError("");
      reset();
      router.replace(updateUrlWithLoginMethod(method));
    },
    [
      router,
      saveLoginMethodToStorage,
      updateUrlWithLoginMethod,
      setSubmitError,
      reset,
    ],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginMethod === "email") {
      mutate({
        type: "email_login",
        identifier: email,
        ...(invitationCode && { invitation_code: invitationCode }),
      });
    } else {
      mutate({
        type: "phone_login",
        country_code: countryCode,
        identifier: countryCode + phoneNumber,
        ...(invitationCode && { invitation_code: invitationCode }),
      });
    }
  };

  const { data: { data: groupLogo } = { data: [] } } = useQuery({
    queryFn: () =>
      getGroupLogo(axiosClient, {
        groupId: groupId || "",
        invitationCode: invitationCode || "",
      }),
    queryKey: ["group", groupId, invitationCode],
    enabled: !!groupId || !!invitationCode,
  });

  useEffect(() => {
    setGroupId(searchParams.get("gid"));

    if (groupId) {
      if (!isEmpty(groupLogo?.white_label_logo)) {
        localStorage.setItem("logo", groupLogo?.white_label_logo);
      }
    }
  }, [searchParams, groupId, groupLogo]);

  useEffect(() => {
    const flag = sessionStorage.getItem("invitation_redirect");
    if (flag === "true" && invitationCode) {
      setIsInvitationRedirect(true);
    }
  }, []);

  const { t } = useTranslation();

  if (isLoading) <DataFetchingSpinner />;
  return (
    <form onSubmit={submit}>
      <Meta title="Login · High Five Academy" />

      {/* Only show UI components when not loading */}
      {!isLoading ? (
        <>
          <div className="mb-1.5 block text-center">
            <Label
              htmlFor={loginMethod === "email" ? "email" : "phone"}
              value={
                !isInvitationRedirect
                  ? loginMethod === "email"
                    ? invitationCode
                      ? t["Login to your account"]
                      : t["Enter your email address to get started"]
                    : invitationCode
                      ? t["Login to your account"]
                      : t["Enter your phone number to get started"]
                  : t["Enter your email to create an account and get started"]
              }
              className="font-dialog text-yellow-900 dark:text-gray-300"
            />
          </div>

          <div className="mb-4 flex justify-center rounded-xl bg-black/5 dark:bg-gray-800 p-1 hidden">
            <button
              type="button"
              onClick={() => switchLoginMethod("email")}
              className={`w-1/2 py-4 px-4 rounded-xl h-[54px] font-medium text-sm focus:outline-none flex items-center justify-center space-x-2 ${
                loginMethod === "email"
                  ? "bg-black dark:bg-gray-700 text-white dark:text-white shadow"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              <MdOutlineEmail className="h-5 w-5" />
              {/* TODO: Add translation for "Email" */}
              <span>{t["Email"]}</span>
            </button>
            <button
              type="button"
              onClick={() => switchLoginMethod("phone")}
              className={`w-1/2 py-4 px-4 rounded-xl font-medium text-sm focus:outline-none flex items-center justify-center space-x-2 ${
                loginMethod === "phone"
                  ? "bg-black dark:bg-gray-700 text-white dark:text-white shadow"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              <MdSmartphone className="h-5 w-5" />
              {/* TODO: Add translation for "Phone Number" */}
              <span>{t["Phone Number"]}</span>
            </button>
          </div>

          {loginMethod === "email" ? (
            <>
              {invitationCode && (
                <label
                  htmlFor="email"
                  className="block mb-2 font-dialog text-sm text-yellow-700 dark:text-yellow-400"
                >
                  Email
                </label>
              )}
              <TextInput
                id="email"
                type="email"
                name="email"
                placeholder="mail@example.com"
                sizing="lg"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                color={isError ? "failure" : ""}
                helperText={error ? <>{submitError}</> : null}
                readOnly={isPending}
                inputMode="email"
                className="[&_input]:rounded-xl [&_input]:bg-gray-50 dark:[&_input]:bg-gray-700 [&_input]:border-gray-300 dark:[&_input]:border-gray-600 dark:[&_input]:text-gray-200 [&_input]:placeholder:text-gray-300 dark:[&_input]:placeholder:text-gray-400 font-dialog [&_input]:py-[0.875rem] [&+p]:font-dialog [&+p]:inline-block mb-4"
                required
                enterKeyHint="done"
              />
            </>
          ) : (
            <>
              {invitationCode && (
                <label
                  htmlFor="email"
                  className="block mb-2 font-dialog text-sm text-yellow-700 dark:text-yellow-400"
                >
                  Phone
                </label>
              )}
              <PhoneInput
                id="phone"
                name="phone"
                placeholder="e.g. +11234567890"
                sizing="lg"
                value={{
                  phoneValue: phoneNumber,
                  countryCodeValue: countryCode,
                  countryCodeISO2Value: countryISO2,
                }}
                onChange={handleOnPhoneInputChange}
                color={isError ? "failure" : ""}
                helperText={error ? <>{submitError}</> : null}
                readOnly={isPending}
                className="[&_input]:rounded-xl [&_input]:bg-gray-50 dark:[&_input]:bg-gray-700 [&_input]:border-gray-300 dark:[&_input]:border-gray-600 dark:[&_input]:text-gray-200 font-dialog [&_input]:py-[0.875rem] [&+p]:font-dialog [&+p]:inline-block mb-4"
                required
              />
            </>
          )}
          {/* Invitation code readonly input */}
          {invitationCode && (
            <>
              <label
                htmlFor="email"
                className="hidden mb-2 font-dialog text-sm text-yellow-700 dark:text-yellow-400"
              >
                Invitation Code
              </label>
              <TextInput
                id="invitationCode"
                type="text"
                name="invitationCode"
                placeholder={t["Invitation Code"]}
                sizing="lg"
                value={invitationCode}
                readOnly
                className="[&_input]:rounded-xl hidden [&_input]:bg-gray-50 dark:[&_input]:bg-gray-700 [&_input]:border-gray-300 dark:[&_input]:border-gray-600 dark:[&_input]:text-gray-200 font-dialog [&_input]:py-[0.875rem] [&+p]:font-dialog [&+p]:inline-block opacity-50"
              />
            </>
          )}
          {/* Error message */}
          {isError && (
            <div
              data-cy="error-message"
              className="text-sm font-dialog inline-block mt-2 text-red-600 dark:text-red-500"
            >
              &nbsp;{" "}
              {loginMethod === "email"
                ? t["Failed to send OTP to email"]
                : t["Failed to send OTP to phone"]}
            </div>
          )}
          <AppButton
            data-cy="login-button"
            text={
              loginMethod === "email"
                ? invitationCode
                  ? t["Continue with email"]
                  : t["Sign in with email"]
                : invitationCode
                  ? t["Continue with phone"]
                  : t["Sign in with phone"]
            }
            className="mt-4"
            disabled={
              isPending ||
              (loginMethod === "email" ? email === "" : phoneNumber === "")
            }
            type="submit"
          />
          <p className="font-dialog text-sm text-center mt-2 text-black/40 dark:text-gray-500">
            {t["By continuing, you acknowledge High Five Academy’s"]}{" "}
            <Link href={"https://poko.ai/privacy"} target="_blank">
              <span className="underline whitespace-nowrap">
                {t["Privacy Policy"]}
              </span>
            </Link>
            .
          </p>
        </>
      ) : (
        // Show a minimal loading state
        <div className="flex justify-center py-10">
          {/* You can add a spinner or loading message here if desired */}
        </div>
      )}
    </form>
  );
}
